/**
 * Avatar Stoneshp: muñeco cabezón que se viste con prendas de la tienda.
 * - StoneshpAvatar.svg(config, { cabeza }) devuelve el dibujo en SVG (entero o solo la cabeza).
 * - La configuración se guarda en el navegador (localStorage 'stoneshp:avatar').
 * - Los elementos con [data-avatar-cabeza] muestran la cabeza del avatar guardado (icono de perfil).
 * El creador está en sections/creador-avatar.liquid.
 */
(() => {
  if (window.StoneshpAvatar) return;

  const CLAVE = 'stoneshp:avatar';

  const PIELES = { clara: '#f3d4ba', media: '#e2ad86', morena: '#b97b53', oscura: '#7b4b2f' };
  const PELOS = { negro: '#1d1a18', castano: '#5b3b25', rubio: '#d9b36b', pelirrojo: '#a9492b', canoso: '#b9b7b2' };
  const ROPA = {
    negro: '#1d1d1d', blanco: '#f1efe9', gris: '#8e9093', marino: '#1f2a45', azul: '#2b56b4', celeste: '#8fb4d8',
    oliva: '#5c6337', verde: '#2f6b46', lima: '#bcd05b', mostaza: '#c99b2f', naranja: '#e0722d', rojo: '#b9272e',
    burdeos: '#6e202d', beige: '#cdbb98',
  };
  const ZAPAS = {
    azul: '#2c59b1', celeste: '#8fb7dd', rojo: '#b9272e', verde: '#2f6b46', burdeos: '#6e202d', negro: '#1d1d1d',
    gris: '#9b9b9b', beige: '#cdbb98', marino: '#1f2a45',
  };

  const PREDETERMINADO = {
    piel: 'media',
    pelo: 'castano',
    sudadera: { marca: 'si', color: 'lima' },
    gorro: { tipo: 'cp', color: 'negro' },
    pantalon: { marca: 'cp', color: 'negro' },
    zapas: { color: 'azul' },
    camiseta: { equipo: '', numero: '' },
    fondo: 'hueso',
  };

  /** Mezcla un color con negro (f < 0) o con blanco (f > 0). */
  const tono = (hex, f) => {
    const n = parseInt(hex.slice(1), 16);
    const c = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => Math.round(f < 0 ? v * (1 + f) : v + (255 - v) * f));
    return '#' + c.map((v) => v.toString(16).padStart(2, '0')).join('');
  };
  /** Los colores muy oscuros se oscurecen poco y se marcan aclarando, para que se vean los pliegues. */
  const sombra = (hex, f = -0.22) => (luz(hex) < 0.12 ? tono(hex, 0.12) : tono(hex, f));
  const luz = (hex) => {
    const n = parseInt(hex.slice(1), 16);
    return (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
  };

  const completar = (c) => {
    const p = PREDETERMINADO;
    c = c && typeof c === 'object' ? c : {};
    return {
      fondo: FONDOS[c.fondo] ? c.fondo : 'hueso',
      piel: PIELES[c.piel] ? c.piel : p.piel,
      pelo: PELOS[c.pelo] ? c.pelo : p.pelo,
      sudadera: {
        marca: c.sudadera?.marca === 'cp' ? 'cp' : c.sudadera?.marca === 'si' ? 'si' : p.sudadera.marca,
        color: ROPA[c.sudadera?.color] ? c.sudadera.color : p.sudadera.color,
      },
      gorro: {
        // tipo: 'ninguno', 'cp' (pasamontañas con gafas C.P. Company) o 'si' (gorro Stone Island). Los avatares antiguos guardaban «puesto».
        tipo: ['ninguno', 'cp', 'si'].includes(c.gorro?.tipo)
          ? c.gorro.tipo
          : typeof c.gorro?.puesto === 'boolean'
            ? (c.gorro.puesto ? 'cp' : 'ninguno')
            : p.gorro.tipo,
        color: ROPA[c.gorro?.color] ? c.gorro.color : p.gorro.color,
      },
      pantalon: {
        marca: c.pantalon?.marca === 'si' ? 'si' : c.pantalon?.marca === 'cp' ? 'cp' : p.pantalon.marca,
        color: ROPA[c.pantalon?.color] ? c.pantalon.color : p.pantalon.color,
      },
      zapas: { color: ZAPAS[c.zapas?.color] ? c.zapas.color : p.zapas.color },
      // Camiseta de fútbol: si hay equipo, se lleva en lugar de la sudadera
      camiseta: {
        equipo: EQUIPOS[c.camiseta?.equipo] ? c.camiseta.equipo : '',
        // Dorsal en el pecho (0-99); vacío = sin número
        numero: /^\d{1,2}$/.test(String(c.camiseta?.numero ?? '')) ? String(Number(c.camiseta.numero)) : '',
      },
    };
  };

  const leer = () => {
    try {
      const v = localStorage.getItem(CLAVE);
      return v ? completar(JSON.parse(v)) : null;
    } catch {
      return null;
    }
  };

  const guardar = (c) => {
    try {
      localStorage.setItem(CLAVE, JSON.stringify(completar(c)));
    } catch {}
    pintarCabezas();
    document.dispatchEvent(new CustomEvent('avatar:cambio'));
  };

  const borrar = () => {
    try {
      localStorage.removeItem(CLAVE);
    } catch {}
    pintarCabezas();
    document.dispatchEvent(new CustomEvent('avatar:cambio'));
  };

  let contador = 0;

  /** Piezas de marca con detalle (necesitan los degradados del SVG, por eso reciben su id). */
  const piezas = (id) => {
    const metal = `url(#${id}m)`;
    const vidrio = `url(#${id}g)`;

    /**
     * Lente de C.P. Company: base cosida, bisel metálico pulido, junta oscura, cristal ahumado y reflejos.
     * Las de las gafas llevan además cuatro tornillos en el aro.
     */
    const lente = (cx, cy, r, base) => {
      const tornillos = r > 8
        ? [45, 135, 225, 315].map((a) => {
            const t = (a * Math.PI) / 180, d = r * 0.9;
            return `<circle cx="${(cx + Math.cos(t) * d).toFixed(2)}" cy="${(cy + Math.sin(t) * d).toFixed(2)}" r="${(r * 0.055).toFixed(2)}" fill="#6d7377"/>`;
          }).join('')
        : '';
      const cosido = base
        ? `<circle cx="${cx}" cy="${cy}" r="${r * 1.32}" fill="${base}"/>
           <circle cx="${cx}" cy="${cy}" r="${r * 1.2}" fill="none" stroke="#000" stroke-opacity="0.35" stroke-width="${r * 0.06}" stroke-dasharray="${r * 0.14} ${r * 0.1}"/>`
        : '';
      return `
        ${cosido}
        <circle cx="${cx + r * 0.08}" cy="${cy + r * 0.12}" r="${r * 1.02}" fill="#000" opacity="0.22"/>
        <circle cx="${cx}" cy="${cy}" r="${r}" fill="${metal}" stroke="#5f6569" stroke-width="${Math.max(0.35, r * 0.04)}"/>
        <circle cx="${cx}" cy="${cy}" r="${r * 0.86}" fill="none" stroke="#fff" stroke-opacity="0.5" stroke-width="${r * 0.05}"/>
        ${tornillos}
        <circle cx="${cx}" cy="${cy}" r="${r * 0.78}" fill="#2a2e31"/>
        <circle cx="${cx}" cy="${cy}" r="${r * 0.68}" fill="${vidrio}"/>
        <path d="M${cx - r * 0.5} ${cy} A${r * 0.5} ${r * 0.5} 0 0 1 ${cx} ${cy - r * 0.5}" fill="none" stroke="#fff" stroke-opacity="0.8" stroke-width="${r * 0.1}" stroke-linecap="round"/>
        <path d="M${cx + r * 0.18} ${cy + r * 0.44} A${r * 0.47} ${r * 0.47} 0 0 0 ${cx + r * 0.44} ${cy + r * 0.18}" fill="none" stroke="#9fc3d1" stroke-opacity="0.45" stroke-width="${r * 0.07}" stroke-linecap="round"/>`;
    };

    /** Gafas del gorro C.P. Company: dos lentes con montura de goma, puente metálico y remaches. */
    const gafas = (cy) => `
      <ellipse cx="100" cy="${cy + 14}" rx="34" ry="3.2" fill="#000" opacity="0.13"/>
      <rect x="57" y="${cy - 3.5}" width="10" height="7" rx="3" fill="#232527"/>
      <rect x="133" y="${cy - 3.5}" width="10" height="7" rx="3" fill="#232527"/>
      <circle cx="80" cy="${cy}" r="16.6" fill="#232527"/>
      <circle cx="120" cy="${cy}" r="16.6" fill="#232527"/>
      <rect x="92" y="${cy - 4.5}" width="16" height="9" fill="#232527"/>
      <rect x="94.5" y="${cy - 3}" width="11" height="6" rx="3" fill="${metal}" stroke="#6f7579" stroke-width="0.4"/>
      <circle cx="61" cy="${cy}" r="2.1" fill="${metal}" stroke="#6f7579" stroke-width="0.4"/>
      <circle cx="139" cy="${cy}" r="2.1" fill="${metal}" stroke="#6f7579" stroke-width="0.4"/>
      ${lente(80, cy, 13.4)}
      ${lente(120, cy, 13.4)}`;

    /**
     * Insignia de Stone Island (diseñada a 32×42 y escalada): dos botones negros, parche negro con la
     * parte de arriba en arco, ribete verde y amarillo, rosa de los vientos con las puntas partidas en
     * dos tonos y el nombre debajo.
     */
    const insigniaSI = (x, y, s = 0.5) => {
      const AMARILLO = '#f2d63a', VERDE = '#3f8f3b', OSCURO = '#c4a71f';
      // Una punta de la rosa partida en dos mitades (una clara y otra oscura), rotada `a` grados
      const punta = (a, largo, ancho, claro, oscuro) => `
        <g transform="rotate(${a} 16 21.5)">
          <path d="M16 ${21.5 - largo} L${16 - ancho} 21.5 H16 Z" fill="${claro}"/>
          <path d="M16 ${21.5 - largo} L${16 + ancho} 21.5 H16 Z" fill="${oscuro}"/>
        </g>`;
      return `
      <g transform="translate(${x} ${y}) scale(${s})">
        <path d="M7 3.2 V10 M25 3.2 V10" stroke="#1a1a1a" stroke-width="1.4"/>
        <circle cx="7" cy="3.4" r="3" fill="#1e1e1e" stroke="#4a4a4a" stroke-width="0.5"/>
        <circle cx="25" cy="3.4" r="3" fill="#1e1e1e" stroke="#4a4a4a" stroke-width="0.5"/>
        <g fill="#5a5a5a"><circle cx="6.2" cy="2.6" r="0.45"/><circle cx="7.8" cy="2.6" r="0.45"/><circle cx="6.2" cy="4.2" r="0.45"/><circle cx="7.8" cy="4.2" r="0.45"/>
          <circle cx="24.2" cy="2.6" r="0.45"/><circle cx="25.8" cy="2.6" r="0.45"/><circle cx="24.2" cy="4.2" r="0.45"/><circle cx="25.8" cy="4.2" r="0.45"/></g>
        <path d="M1.4 12.2 Q16 1.6 30.6 12.2 V38 Q30.6 41.2 27.4 41.2 H4.6 Q1.4 41.2 1.4 38 Z" fill="#0f0f0f" stroke="${VERDE}" stroke-width="1.9"/>
        <path d="M3.9 13.3 Q16 4.6 28.1 13.3 V37.4 Q28.1 38.8 26.7 38.8 H5.3 Q3.9 38.8 3.9 37.4 Z" fill="none" stroke="${AMARILLO}" stroke-width="0.9"/>
        <circle cx="16" cy="21.5" r="5.4" fill="none" stroke="${AMARILLO}" stroke-width="0.45" opacity="0.8"/>
        ${[45, 135, 225, 315].map((a) => punta(a, 5.6, 1.15, AMARILLO, VERDE)).join('')}
        ${[0, 90, 180, 270].map((a) => punta(a, 9, 1.9, AMARILLO, OSCURO)).join('')}
        <circle cx="16" cy="21.5" r="0.9" fill="#0f0f0f"/>
        <text x="16" y="35.4" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="3.5" fill="${AMARILLO}" textLength="21" lengthAdjust="spacingAndGlyphs">STONE ISLAND</text>
      </g>`;
    };

    /**
     * Fotos reales de las piezas (recortadas, con transparencia) colocadas como si estuvieran cosidas:
     * perspectiva (giro, inclinación y estrechamiento), sombra sobre la tela y luz del volumen.
     * Las direcciones las pone snippets/header-actions.liquid en window.StoneshpAvatarImgs.
     */
    let n = 0;
    const foto = (url, w, h, { x, y, rot = 0, ex = 1, sk = 0, luz = 0.4, encima = '' }) => {
      const m = `${id}k${++n}`;
      const caja = `x="${-w / 2}" y="${-h / 2}" width="${w}" height="${h}"`;
      return `
        <g transform="translate(${x} ${y}) rotate(${rot}) skewY(${sk}) scale(${ex} 1)">
          <mask id="${m}" maskUnits="userSpaceOnUse" ${caja} style="mask-type:alpha"><image href="${url}" ${caja} preserveAspectRatio="none"/></mask>
          <g filter="url(#${id}s)"><image href="${url}" ${caja} preserveAspectRatio="none"/>${encima}</g>
          <rect ${caja} fill="url(#${id}l)" opacity="${luz}" mask="url(#${m})"/>
        </g>`;
    };

    /** Botón negro de cuatro agujeros (los de la insignia de Stone Island, metidos en sus ojales). */
    const boton = (bx, by, r) => `
      <circle cx="${bx}" cy="${by}" r="${r}" fill="#141414" stroke="#3b3b3b" stroke-width="${r * 0.16}"/>
      <circle cx="${bx - r * 0.3}" cy="${by - r * 0.32}" r="${r * 0.42}" fill="#fff" opacity="0.12"/>
      ${[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([a, b]) => `<circle cx="${bx + a * r * 0.3}" cy="${by + b * r * 0.3}" r="${r * 0.13}" fill="#3c3c3c"/>`).join('')}`;

    /** Insignia real de Stone Island (foto de 358×195) con sus dos botones en los ojales. */
    const parcheSI = (url, ancho, pos) => {
      const alto = (ancho * 195) / 358, r = ancho * 0.04;
      const encima = boton(-ancho / 2 + ancho * 0.134, -alto / 2 + alto * 0.487, r) + boton(-ancho / 2 + ancho * 0.846, -alto / 2 + alto * 0.431, r);
      return foto(url, ancho, alto, { ...pos, encima });
    };

    return { lente, gafas, insigniaSI, foto, parcheSI };
  };

  /**
   * Camisetas de fútbol (caricatura): el dibujo de la camiseta de cada equipo, su escudo en pequeño
   * sobre el pecho y «STONESHP» como patrocinador. Los escudos son caricaturas simplificadas, no los oficiales.
   * Cada escudo se dibuja en una caja de -10 a 10 y se escala al colocarlo.
   */
  const estrella = (cx, cy, r, color) => {
    const pts = Array.from({ length: 10 }, (_, i) => {
      const a = (Math.PI / 5) * i - Math.PI / 2, rr = i % 2 ? r * 0.42 : r;
      return `${(cx + Math.cos(a) * rr).toFixed(2)},${(cy + Math.sin(a) * rr).toFixed(2)}`;
    }).join(' ');
    return `<polygon points="${pts}" fill="${color}"/>`;
  };
  const corona = (cy, color) => `<path d="M-5 ${cy} L-5.6 ${cy - 4} L-2.6 ${cy - 2} L0 ${cy - 5} L2.6 ${cy - 2} L5.6 ${cy - 4} L5 ${cy} Z" fill="${color}" stroke="#7a5c00" stroke-width="0.35"/>`;
  const ESCUDO = 'M-8 -9 H8 V1 Q8 7.5 0 10 Q-8 7.5 -8 1 Z';
  const letras = (t, y, size, color, extra = '') =>
    `<text x="0" y="${y}" text-anchor="middle" font-family="Arial Black, Arial, Helvetica, sans-serif" font-weight="900" font-size="${size}" fill="${color}" ${extra}>${t}</text>`;

  const EQUIPOS = {
    dortmund: {
      nombre: 'Borussia Dortmund', base: '#f7d117', ribete: '#111111', patron: { tipo: 'liso' },
      escudo: () => `<circle r="9.4" fill="#f7d117" stroke="#111" stroke-width="2"/><circle r="6.6" fill="none" stroke="#111" stroke-width="0.6"/>${letras('BVB', 2.1, 5.6, '#111')}${letras('09', 6.4, 2.6, '#111')}`,
    },
    milan: {
      nombre: 'AC Milan', base: '#111111', ribete: '#111111', patron: { tipo: 'rayas', color: '#c8102e', ancho: 9 },
      escudo: (u) => `<clipPath id="${u}"><rect x="-8" y="-9.5" width="16" height="19" rx="6.5"/></clipPath>
        <g clip-path="url(#${u})"><rect x="-8" y="-10" width="16" height="20" fill="#fff"/>
          <rect x="-8" y="-10" width="2.7" height="20" fill="#c8102e"/><rect x="-5.3" y="-10" width="2.7" height="20" fill="#111"/><rect x="-2.6" y="-10" width="2.6" height="20" fill="#c8102e"/>
          <rect x="3.1" y="-10" width="1.8" height="20" fill="#c8102e"/><rect x="0" y="-1.3" width="8" height="1.8" fill="#c8102e"/></g>
        <rect x="-8" y="-9.5" width="16" height="19" rx="6.5" fill="none" stroke="#111" stroke-width="1"/>`,
    },
    lazio: {
      nombre: 'Lazio', base: '#87c4ea', ribete: '#ffffff', patron: { tipo: 'liso' },
      escudo: () => `<path d="${ESCUDO}" fill="#87c4ea" stroke="#fff" stroke-width="1.2"/>
        <path d="M0 -4.5 L-7 -1.2 L-3.6 -0.8 L-5.4 3.2 L0 0.4 L5.4 3.2 L3.6 -0.8 L7 -1.2 Z" fill="#fff"/>
        <circle cx="0" cy="-5.4" r="1.6" fill="#fff"/><path d="M-1.6 2 L0 7 L1.6 2 Z" fill="#fff"/>`,
    },
    galatasaray: {
      nombre: 'Galatasaray', base: '#a90432', ribete: '#ffcd00', patron: { tipo: 'mitades', izq: '#ffcd00', der: '#a90432' },
      escudo: () => `<circle r="9.4" fill="#ffcd00" stroke="#a90432" stroke-width="2"/>${letras('GS', 3, 8.4, '#a90432')}`,
    },
    boca: {
      nombre: 'Boca Juniors', base: '#0b2a6f', ribete: '#f6c518', patron: { tipo: 'franja', color: '#f6c518' },
      escudo: () => `<path d="M-8 -9 H8 V2 Q8 8 0 10 Q-8 8 -8 2 Z" fill="#0b2a6f" stroke="#f6c518" stroke-width="1.2"/>
        <rect x="-8" y="-2.4" width="16" height="4.8" fill="#f6c518"/>${letras('CABJ', 1.6, 3.8, '#0b2a6f')}
        ${estrella(-4, -5.6, 1.4, '#f6c518')}${estrella(0, -5.6, 1.4, '#f6c518')}${estrella(4, -5.6, 1.4, '#f6c518')}`,
    },
    roma: {
      nombre: 'AS Roma', base: '#8e1f2f', ribete: '#f2a900', patron: { tipo: 'liso' },
      escudo: () => `<path d="${ESCUDO}" fill="#8e1f2f" stroke="#f2a900" stroke-width="1.3"/>
        <path d="M-6 -1 Q-3 -6 2 -4.6 L5.4 -6.4 L4.6 -2.6 Q6 1 3 2.6 L-4 2.6 Q-6.4 1.6 -6 -1 Z" fill="#f2a900"/>${letras('ASR', 7.6, 3.6, '#f2a900')}`,
    },
    marsella: {
      nombre: 'Olympique de Marsella', base: '#f4f6f8', ribete: '#2faee0', patron: { tipo: 'liso' },
      escudo: () => `<path d="M-8 -7 H8 V2 Q8 8 0 10 Q-8 8 -8 2 Z" fill="#fff" stroke="#2faee0" stroke-width="1.4"/>${letras('OM', 4.6, 8, '#2faee0')}${estrella(0, -10.4, 2.4, '#d4a73a')}`,
    },
    barcelona: {
      nombre: 'FC Barcelona', base: '#a50044', ribete: '#edbb00', patron: { tipo: 'rayas', color: '#004d98', ancho: 10 },
      escudo: (u) => `<clipPath id="${u}"><path d="M-8 -9 H8 V-1 Q8 6.5 0 10 Q-8 6.5 -8 -1 Z"/></clipPath>
        <g clip-path="url(#${u})"><rect x="-8" y="-9" width="8" height="7" fill="#fff"/><rect x="-4.9" y="-9" width="1.8" height="7" fill="#c8102e"/><rect x="-8" y="-6.4" width="8" height="1.8" fill="#c8102e"/>
          <rect x="0" y="-9" width="8" height="7" fill="#f6c518"/>${[1.2, 3.6, 6].map((x) => `<rect x="${x}" y="-9" width="1.2" height="7" fill="#c8102e"/>`).join('')}
          <rect x="-8" y="-2" width="16" height="12" fill="#a50044"/>${[-6, -2, 2, 6].map((x) => `<rect x="${x - 1}" y="-2" width="2" height="12" fill="#004d98"/>`).join('')}
          <rect x="-8" y="-2.6" width="16" height="1.4" fill="#f6c518"/><circle cx="0" cy="4" r="2.2" fill="#f6c518"/></g>
        <path d="M-8 -9 H8 V-1 Q8 6.5 0 10 Q-8 6.5 -8 -1 Z" fill="none" stroke="#111" stroke-width="0.9"/>`,
    },
    madrid: {
      nombre: 'Real Madrid', base: '#ffffff', ribete: '#d4af37', patron: { tipo: 'liso' },
      escudo: (u) => `<clipPath id="${u}"><circle cy="1.6" r="7.6"/></clipPath>
        <circle cy="1.6" r="7.6" fill="#fff"/><g clip-path="url(#${u})"><path d="M-9 -2 L-6 -6 L9 6 L6 10 Z" fill="#3d2a8f"/></g>
        <circle cy="1.6" r="7.6" fill="none" stroke="#d4af37" stroke-width="1.4"/>${letras('MCF', 3.4, 4.4, '#d4af37', 'stroke="#7a5c00" stroke-width="0.25"')}${corona(-5.4, '#d4af37')}`,
    },
    betis: {
      nombre: 'Real Betis', base: '#ffffff', ribete: '#0b8a3e', patron: { tipo: 'rayas', color: '#0b8a3e', ancho: 9 },
      escudo: (u) => `<clipPath id="${u}"><path d="M-7.5 -6 H7.5 V2 Q7.5 8 0 10 Q-7.5 8 -7.5 2 Z"/></clipPath>
        <g clip-path="url(#${u})"><rect x="-8" y="-6" width="16" height="16" fill="#fff"/>${[-5, -1, 3].map((x) => `<rect x="${x}" y="-6" width="2" height="16" fill="#0b8a3e"/>`).join('')}</g>
        <path d="M-7.5 -6 H7.5 V2 Q7.5 8 0 10 Q-7.5 8 -7.5 2 Z" fill="none" stroke="#0b8a3e" stroke-width="1.1"/>${corona(-6.2, '#d4af37')}`,
    },
    napoles: {
      nombre: 'Nápoles', base: '#12a0d7', ribete: '#ffffff', patron: { tipo: 'liso' },
      escudo: () => `<circle r="9.4" fill="#0e7fb0" stroke="#fff" stroke-width="1.6"/><circle r="6.8" fill="none" stroke="#fff" stroke-width="0.9"/>${letras('N', 4.2, 11, '#fff')}`,
    },
    juventus: {
      nombre: 'Juventus', base: '#ffffff', ribete: '#111111', patron: { tipo: 'rayas', color: '#111111', ancho: 9 },
      escudo: () => `<rect x="-7.4" y="-9.4" width="14.8" height="18.8" rx="2" fill="#fff" stroke="#111" stroke-width="0.8"/>
        <path d="M-3.1 -7.2 H-0.7 V4.6 Q-0.7 7.4 -4 7.4 H-5.2 V5.2 H-4 Q-3.1 5.2 -3.1 4.2 Z" fill="#111"/>
        <path d="M0.7 -7.2 H3.1 V4 Q3.1 8.6 -2.6 8.8 V7.9 Q0.7 7.4 0.7 4 Z" fill="#111"/>`,
    },
    river: {
      nombre: 'River Plate', base: '#ffffff', ribete: '#111111', patron: { tipo: 'banda', color: '#e1062c' },
      escudo: (u) => `<clipPath id="${u}"><path d="${ESCUDO}"/></clipPath>
        <path d="${ESCUDO}" fill="#fff"/><g clip-path="url(#${u})"><path d="M-12 -6 L-6 -12 L12 6 L6 12 Z" fill="#e1062c"/></g>
        <path d="${ESCUDO}" fill="none" stroke="#111" stroke-width="1"/>`,
    },
    porto: {
      nombre: 'FC Porto', base: '#ffffff', ribete: '#0b3d91', patron: { tipo: 'rayas', color: '#0b3d91', ancho: 10 },
      escudo: () => `<path d="M-8 -6 H8 V2 Q8 8 0 10 Q-8 8 -8 2 Z" fill="#0b3d91" stroke="#d4af37" stroke-width="1"/>
        <path d="M-8 -6 H0 V10 Q-8 8 -8 2 Z" fill="#fff" opacity="0.15"/>${letras('FCP', 3.6, 5, '#fff')}${corona(-6.6, '#d4af37')}`,
    },
    psg: {
      nombre: 'Paris Saint-Germain', base: '#0b1f4b', ribete: '#da291c', patron: { tipo: 'centro', color: '#da291c', borde: '#ffffff' },
      escudo: () => `<circle r="9.4" fill="#0b1f4b" stroke="#da291c" stroke-width="1"/><circle r="6.2" fill="#da291c"/>
        <path d="M0 -6 L1 -1 L2.6 5.6 H1.3 Q0 2.6 -1.3 5.6 H-2.6 L-1 -1 Z" fill="#fff"/><rect x="-1.6" y="0.6" width="3.2" height="0.8" fill="#fff"/>`,
    },
    olympiacos: {
      nombre: 'Olympiacos', base: '#ffffff', ribete: '#d50032', patron: { tipo: 'rayas', color: '#d50032', ancho: 8 },
      escudo: () => `<circle r="9.4" fill="#fff" stroke="#d50032" stroke-width="1.6"/>
        <path d="M-5.6 5 Q-8 0 -5.4 -5" fill="none" stroke="#2e8b3d" stroke-width="1.5" stroke-dasharray="1.6 0.6"/>
        <path d="M5.6 5 Q8 0 5.4 -5" fill="none" stroke="#2e8b3d" stroke-width="1.5" stroke-dasharray="1.6 0.6"/>
        <path d="M-2.6 5 Q-3.6 -1 0 -4.4 Q3.6 -2.6 2.6 0.6 L1.4 1 L1.8 5 Z" fill="#d50032"/>`,
    },
    estrellaroja: {
      nombre: 'Estrella Roja', base: '#ffffff', ribete: '#c8102e', patron: { tipo: 'rayas', color: '#c8102e', ancho: 9 },
      escudo: () => `<circle r="9.4" fill="#fff" stroke="#c8102e" stroke-width="1.8"/>${estrella(0, 0.6, 7, '#c8102e')}`,
    },
    fenerbahce: {
      nombre: 'Fenerbahçe', base: '#ffed00', ribete: '#0b1c52', patron: { tipo: 'rayas', color: '#0b1c52', ancho: 9 },
      escudo: (u) => `<clipPath id="${u}"><circle r="7"/></clipPath>
        <circle r="9.4" fill="#fff" stroke="#0b1c52" stroke-width="1.4"/>
        <g clip-path="url(#${u})"><rect x="-8" y="-8" width="16" height="16" fill="#ffed00"/><rect x="0" y="-8" width="8" height="16" fill="#0b1c52"/><path d="M-1.4 7 Q0 3 1.4 7 Z" fill="#2e8b3d"/></g>
        <circle r="7" fill="none" stroke="#0b1c52" stroke-width="0.6"/>`,
    },
    celta: {
      nombre: 'Celta de Vigo', base: '#8ac6ee', ribete: '#ffffff', patron: { tipo: 'liso' },
      escudo: () => `<path d="M-7.6 -5.6 H7.6 V2 Q7.6 8 0 10 Q-7.6 8 -7.6 2 Z" fill="#8ac6ee" stroke="#fff" stroke-width="1.2"/>
        <path d="M-1.2 -3.6 H1.2 V-0.6 H4.4 V1.6 H1.2 V7.2 H-1.2 V1.6 H-4.4 V-0.6 H-1.2 Z" fill="#d7192b" stroke="#fff" stroke-width="0.4"/>
        ${corona(-6.2, '#d4af37')}`,
    },
    deportivo: {
      nombre: 'Deportivo de La Coruña', base: '#ffffff', ribete: '#0050a0', patron: { tipo: 'rayas', color: '#0050a0', ancho: 9 },
      escudo: (u) => `<clipPath id="${u}"><circle cy="1.4" r="7.6"/></clipPath>
        <circle cy="1.4" r="7.6" fill="#fff"/>
        <g clip-path="url(#${u})"><path d="M-10 4.6 L5.2 -10 L10 -5.4 L-5.2 9.6 Z" fill="#0050a0"/></g>
        <circle cy="1.4" r="7.6" fill="none" stroke="#0050a0" stroke-width="1.2"/>
        <text x="0" y="3.2" text-anchor="middle" transform="rotate(-44 0 1.4)" font-family="Arial Black, Arial, Helvetica, sans-serif" font-weight="900" font-size="3.6" fill="#fff">RCD</text>
        ${corona(-5.8, '#d4af37')}`,
    },
  };

  /** Dibujo de la camiseta (sin brazos): silueta, patrón, ribetes, cuello, escudo y patrocinador. */
  const camisetaFutbol = (clave, id, piel = '#e2ad86', numero = '') => {
    const E = EQUIPOS[clave];
    if (!E) return '';
    const cuerpo = 'M58 128 H142 Q146 128 146 134 V203 Q146 207 141 207 H59 Q54 207 54 203 V134 Q54 128 58 128 Z';
    const mangaI = 'M44 140 Q46 130 60 128 L64 128 L63 165 H41 Z';
    const mangaD = 'M156 140 Q154 130 140 128 L136 128 L137 165 H159 Z';
    const P = E.patron;
    let patron = '';
    if (P.tipo === 'rayas') {
      const w = P.ancho;
      for (let x = 100 - w / 2 - 2 * w * 6; x < 172; x += 2 * w) patron += `<rect x="${x}" y="118" width="${w}" height="100" fill="${P.color}"/>`;
    } else if (P.tipo === 'mitades') {
      patron = `<rect x="30" y="118" width="70" height="100" fill="${P.izq}"/><rect x="100" y="118" width="70" height="100" fill="${P.der}"/>`;
    } else if (P.tipo === 'franja') {
      patron = `<rect x="30" y="146" width="140" height="17" fill="${P.color}"/>`;
    } else if (P.tipo === 'banda') {
      patron = `<path d="M44 124 L68 124 L162 196 L162 216 L140 216 L44 142 Z" fill="${P.color}"/>`;
    } else if (P.tipo === 'centro') {
      patron = `<rect x="84" y="118" width="32" height="100" fill="${P.borde}"/><rect x="88" y="118" width="24" height="100" fill="${P.color}"/>`;
    }
    const oscuro = luz(E.base) < 0.5;
    return `
      <clipPath id="${id}t"><path d="${cuerpo}"/><path d="${mangaI}"/><path d="${mangaD}"/></clipPath>
      <g clip-path="url(#${id}t)">
        <rect x="30" y="118" width="140" height="100" fill="${E.base}"/>
        ${patron}
        <rect x="30" y="118" width="140" height="100" fill="url(#${id}v)"/>
        <rect x="38" y="160.5" width="28" height="5" fill="${E.ribete}"/>
        <rect x="134" y="160.5" width="28" height="5" fill="${E.ribete}"/>
        <rect x="50" y="203" width="100" height="5" fill="${oscuro ? '#fff' : '#000'}" opacity="0.08"/>
      </g>
      <path d="M62 136 Q62 150 61.5 164" stroke="#000" stroke-opacity="0.18" stroke-width="1" fill="none"/>
      <path d="M138 136 Q138 150 138.5 164" stroke="#000" stroke-opacity="0.18" stroke-width="1" fill="none"/>
      <path d="M89 128 L100 141 L111 128 Z" fill="${piel}"/>
      <path d="M88 128 L100 142.5 L112 128" fill="none" stroke="${E.ribete}" stroke-width="3" stroke-linejoin="round"/>
      <g transform="translate(123 151) scale(0.62)">${E.escudo(`${id}e`)}</g>
      ${numero ? `<text x="77" y="155.6" text-anchor="middle" font-family="'Arial Black', Arial, Helvetica, sans-serif" font-weight="900" font-size="12.5"
        fill="${E.numero || (luz(E.base) < 0.55 ? '#ffffff' : '#141414')}" stroke="${E.numero ? '#141414' : (luz(E.base) < 0.55 ? '#141414' : '#ffffff')}" stroke-width="1.6" stroke-linejoin="round" paint-order="stroke"
        ${numero.length > 1 ? 'textLength="14" lengthAdjust="spacingAndGlyphs"' : ''}>${numero}</text>` : ''}
      <text x="100" y="179" text-anchor="middle" font-family="Arial Black, Arial, Helvetica, sans-serif" font-weight="900" font-size="11"
        letter-spacing="0.4" fill="#f5cf1f" stroke="#1d1d1d" stroke-width="1.2" stroke-linejoin="round" paint-order="stroke"
        textLength="56" lengthAdjust="spacingAndGlyphs">STONESHP</text>`;
  };

  /** Miniatura de una camiseta para el creador. */
  let contadorMini = 0;
  const miniCamiseta = (clave) => {
    const id = 'mc' + ++contadorMini;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="36 124 128 86" aria-hidden="true" focusable="false">
      <defs><linearGradient id="${id}v" x1="0" x2="1"><stop offset="0" stop-color="#fff" stop-opacity="0.14"/><stop offset="0.45" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity="0.16"/></linearGradient></defs>
      ${camisetaFutbol(clave, id, '#ece9e3')}</svg>`;
  };

  /**
   * @param {object} config
   * @param {{ cabeza?: boolean, titulo?: string }} [opciones]
   */
  const svg = (config, opciones = {}) => {
    const c = completar(config);
    const id = 'av' + ++contador;
    const piel = PIELES[c.piel];
    const pelo = PELOS[c.pelo];
    const sud = ROPA[c.sudadera.color];
    const gor = ROPA[c.gorro.color];
    const pan = ROPA[c.pantalon.color];
    const zap = ZAPAS[c.zapas.color];
    const vol = `url(#${id}v)`;
    const { lente, gafas, insigniaSI, foto, parcheSI } = piezas(id);
    const F = window.StoneshpAvatarImgs || {};

    const zapatilla = `
      <path d="M100 277 V262 Q100 255 91 255 L77 256 Q64 258 58 267 Q54 274 59 277 Z" fill="${zap}"/>
      <path d="M58 267 Q54 274 59 277 H70 Q66 271 70 262 Q63 262 58 267 Z" fill="${sombra(zap, -0.16)}"/>
      <g stroke="#f4f2ec" stroke-width="2.6" stroke-linecap="round">
        <path d="M79 259 L75 274"/><path d="M84.5 258 L80.5 274"/><path d="M90 257.5 L86 274"/>
      </g>
      <path d="M55 277 H101 V281 Q101 284 98 284 H60 Q54.5 284 54.5 280 Z" fill="#b97a3e"/>
      <path d="M55 281.5 H101" stroke="#8f5a2a" stroke-width="1.4"/>`;

    const bolsillo = (x, marca) => `
      <rect x="${x}" y="222" width="15" height="19" rx="3" fill="${sombra(pan, -0.1)}"/>
      <path d="M${x - 0.5} 222 H${x + 15.5} V227.5 Q${x + 7.5} 230 ${x - 0.5} 227.5 Z" fill="${sombra(pan)}"/>
      ${marca === 'cp' ? (F.lenteCP ? foto(F.lenteCP, 9, 9, { x: x + 7.5, y: 234.5, ex: 0.9, sk: 3, luz: 0.25 }) : lente(x + 7.5, 234.5, 4.4, sombra(pan, -0.32))) : ''}`;

    const arribaFutbol = `
      <!-- Camiseta de fútbol: brazos al aire y manga corta -->
      <path d="M42 164 H62 L61.5 197 H41.5 Z" fill="${piel}"/>
      <path d="M138 164 H158 L158.5 197 H138.5 Z" fill="${piel}"/>
      <path d="M42 164 H62 L61.5 197 H41.5 Z M138 164 H158 L158.5 197 H138.5 Z" fill="${vol}" opacity="0.6"/>
      <circle cx="51" cy="201" r="8.5" fill="${piel}"/>
      <circle cx="149" cy="201" r="8.5" fill="${piel}"/>
      ${camisetaFutbol(c.camiseta.equipo, id, piel, c.camiseta.numero)}`;
    const arribaSudadera = `
      <!-- Sudadera -->
      <path d="M44 140 Q46 130 60 128 L64 128 L62 190 H40 Z" fill="${sud}"/>
      <path d="M156 140 Q154 130 140 128 L136 128 L138 190 H160 Z" fill="${sud}"/>
      <path d="M44 140 Q46 130 60 128 L64 128 L62 190 H40 Z" fill="${vol}"/>
      <path d="M156 140 Q154 130 140 128 L136 128 L138 190 H160 Z" fill="${vol}"/>
      <rect x="39.5" y="185" width="23" height="10" rx="4" fill="${sombra(sud)}"/>
      <rect x="137.5" y="185" width="23" height="10" rx="4" fill="${sombra(sud)}"/>
      <circle cx="51" cy="201" r="8.5" fill="${piel}"/>
      <circle cx="149" cy="201" r="8.5" fill="${piel}"/>
      <path d="M58 128 H142 Q146 128 146 134 V204 Q146 208 141 208 H59 Q54 208 54 204 V134 Q54 128 58 128 Z" fill="${sud}"/>
      <path d="M58 128 H142 Q146 128 146 134 V204 Q146 208 141 208 H59 Q54 208 54 204 V134 Q54 128 58 128 Z" fill="${vol}"/>
      <rect x="54" y="196" width="92" height="12" rx="5" fill="${sombra(sud)}"/>
      <g stroke="${sombra(sud, -0.32)}" stroke-width="0.9" opacity="0.5">
        ${Array.from({ length: 17 }, (_, i) => `<path d="M${59 + i * 5.2} 197.5 V206.5"/>`).join('')}
      </g>
      <path d="M62 140 Q62 160 60 190" stroke="${sombra(sud, -0.3)}" stroke-width="1" fill="none" opacity="0.45"/>
      <path d="M138 140 Q138 160 140 190" stroke="${sombra(sud, -0.3)}" stroke-width="1" fill="none" opacity="0.45"/>
      ${c.sudadera.marca === 'si'
        ? (F.parcheSI ? `<g clip-path="url(#${id}b)">${parcheSI(F.parcheSI, 21, { x: 151.3, y: 150, rot: -1.5, ex: 0.66, sk: -3, luz: 0.32 })}</g>` : insigniaSI(140.5, 142.5, 0.5))
        : (F.lenteCP ? `<g clip-path="url(#${id}b)">${foto(F.lenteCP, 10.5, 10.5, { x: 151.4, y: 157, rot: -1.5, ex: 0.68, sk: -3, luz: 0.3 })}</g>` : lente(148.8, 154, 6.2, sombra(sud, -0.32)))}`;

    const cuerpo = `
      <ellipse cx="100" cy="285" rx="56" ry="6.5" fill="#000" opacity="0.12"/>

      <!-- Pantalón -->
      <rect x="97" y="210" width="6" height="50" fill="${tono(pan, -0.45)}"/>
      <path d="M63 200 H137 V256 Q137 262 131 262 H105 Q101 262 101 256 V214 H99 V256 Q99 262 95 262 H69 Q63 262 63 256 Z" fill="${pan}"/>
      <path d="M63 200 H137 V256 Q137 262 131 262 H105 Q101 262 101 256 V214 H99 V256 Q99 262 95 262 H69 Q63 262 63 256 Z" fill="${vol}"/>
      <rect x="63" y="250" width="36" height="12" rx="5" fill="${sombra(pan)}"/>
      <rect x="101" y="250" width="36" height="12" rx="5" fill="${sombra(pan)}"/>
      <g stroke="${sombra(pan, -0.3)}" stroke-width="0.9" opacity="0.55">
        <path d="M68 251 V261"/><path d="M73 251 V261"/><path d="M78 251 V261"/><path d="M83 251 V261"/><path d="M88 251 V261"/><path d="M93 251 V261"/>
        <path d="M106 251 V261"/><path d="M111 251 V261"/><path d="M116 251 V261"/><path d="M121 251 V261"/><path d="M126 251 V261"/><path d="M131 251 V261"/>
      </g>
      ${bolsillo(64.5, c.pantalon.marca)}
      ${bolsillo(120.5, c.pantalon.marca === 'cp' ? '' : c.pantalon.marca)}
      ${c.pantalon.marca === 'si' ? (F.parcheSI ? parcheSI(F.parcheSI, 17, { x: 128.4, y: 233.5, rot: -2, ex: 0.88, sk: -4, luz: 0.3 }) : insigniaSI(121.3, 226.5, 0.42)) : ''}

      <!-- Zapatillas -->
      <g transform="translate(-2.5 0)">${zapatilla}</g>
      <g transform="translate(202.5 0) scale(-1 1)">${zapatilla}</g>

      ${c.camiseta.equipo ? arribaFutbol : arribaSudadera}`;

    const conGorro = c.gorro.tipo !== 'ninguno';
    const pelo_ = conGorro
      ? `<path d="M37 80 Q36 66 42 60 L44 86 Z M163 80 Q164 66 158 60 L156 86 Z" fill="${pelo}"/>`
      : `<path d="M35 84 Q30 18 100 16 Q170 18 165 84 Q160 66 150 58 Q128 66 104 54 Q82 66 58 58 Q42 66 35 84 Z" fill="${pelo}"/>
         <path d="M58 58 Q70 44 100 42 Q86 50 82 60 Z" fill="#fff" opacity="0.12"/>`;

    /** Cúpula y vuelta de punto, comunes a los dos gorros (el de Stone Island lleva el canalé más fino). */
    const punto = (fino) => `
      <path d="M31 76 Q28 4 100 4 Q172 4 169 76 Z" fill="${gor}"/>
      <path d="M31 76 Q28 4 100 4 Q172 4 169 76 Z" fill="${vol}"/>
      <g stroke="${sombra(gor, -0.28)}" stroke-width="${fino ? 0.8 : 1}" opacity="0.4" fill="none" clip-path="url(#${id}d)">
        ${(fino
          ? [-60, -48, -36, -24, -12, 0, 12, 24, 36, 48, 60]
          : [-40, -20, 0, 20, 40]
        ).map((d) => `<path d="M${100 + d * 0.3} 5 Q${100 + d * 0.95} 30 ${100 + d * 1.25} 58"/>`).join('')}
      </g>
      <rect x="28" y="52" width="144" height="26" rx="12" fill="${sombra(gor, -0.14)}"/>
      <path d="M30 58 Q100 50 170 58" stroke="#fff" stroke-opacity="0.12" stroke-width="2" fill="none"/>
      <g stroke="${sombra(gor, -0.36)}" stroke-width="${fino ? 0.8 : 1}" opacity="0.5">
        ${Array.from({ length: fino ? 40 : 27 }, (_, i) => `<path d="M${35 + i * (fino ? 3.45 : 5.1)} 55 V75"/>`).join('')}
      </g>`;

    /**
     * Pasamontañas con gafas de C.P. Company: cubre cabeza, orejas y cuello; las gafas van sobre los ojos
     * y queda una abertura de canalé para la nariz y la boca.
     */
    const pasamontanas = () => `
      <path d="M71 112 H129 L134 134 Q100 143 66 134 Z" fill="${gor}"/>
      <ellipse cx="36.3" cy="94.5" rx="9" ry="12" fill="${sombra(gor, -0.1)}"/>
      <ellipse cx="163.7" cy="94.5" rx="9" ry="12" fill="${sombra(gor, -0.1)}"/>
      <rect x="32" y="14" width="136" height="122" rx="54" fill="${gor}"/>
      <path d="M71 112 H129 L134 134 Q100 143 66 134 Z" fill="${vol}"/>
      <rect x="32" y="14" width="136" height="122" rx="54" fill="${vol}"/>
      <rect x="32" y="14" width="136" height="122" rx="54" fill="url(#${id}c)" opacity="0.7"/>
      <g stroke="${sombra(gor, -0.3)}" stroke-width="0.9" opacity="0.32" clip-path="url(#${id}p)">
        ${Array.from({ length: 31 }, (_, i) => `<path d="M${31 + i * 4.6} 12 V144"/>`).join('')}
      </g>
      <path d="M67 131 Q100 139.5 133 131" stroke="${sombra(gor, -0.32)}" stroke-width="1.1" fill="none" opacity="0.55"/>
      <ellipse cx="100" cy="115" rx="27.5" ry="16.5" fill="${sombra(gor, -0.2)}"/>
      <g stroke="${sombra(gor, -0.4)}" stroke-width="0.8" opacity="0.45" fill="none">
        ${Array.from({ length: 24 }, (_, i) => { const t = (i / 24) * Math.PI * 2; return `<path d="M${(100 + Math.cos(t) * 24.5).toFixed(1)} ${(115 + Math.sin(t) * 14).toFixed(1)} L${(100 + Math.cos(t) * 27.3).toFixed(1)} ${(115 + Math.sin(t) * 16.3).toFixed(1)}"/>`; }).join('')}
      </g>
      <ellipse cx="100" cy="115" rx="24.5" ry="14" fill="${piel}"/>
      <ellipse cx="100" cy="115" rx="24.5" ry="14" fill="none" stroke="#000" stroke-opacity="0.18" stroke-width="2.2"/>
      <ellipse cx="100" cy="110" rx="3.4" ry="2.2" fill="${tono(piel, -0.18)}"/>
      <ellipse cx="85" cy="117" rx="6" ry="3.6" fill="#e0675a" opacity="0.14"/>
      <ellipse cx="115" cy="117" rx="6" ry="3.6" fill="#e0675a" opacity="0.14"/>
      ${F.gafasCP ? foto(F.gafasCP, 82, (82 * 201) / 520, { x: 100, y: 93, luz: 0.2 }) : gafas(93)}`;

    const gorro =
      c.gorro.tipo === 'cp' ? pasamontanas()
      : c.gorro.tipo === 'si' ? `${punto(true)}${F.parcheSI ? parcheSI(F.parcheSI, 34, { x: 100, y: 64.5, luz: 0.18 }) : insigniaSI(88.8, 46, 0.7)}`
      : '';

    const cabeza = `
      <ellipse cx="36" cy="94" rx="8" ry="11" fill="${tono(piel, -0.08)}"/>
      <ellipse cx="164" cy="94" rx="8" ry="11" fill="${tono(piel, -0.08)}"/>
      <rect x="36" y="18" width="128" height="116" rx="52" fill="${piel}"/>
      <rect x="36" y="18" width="128" height="116" rx="52" fill="url(#${id}c)"/>
      ${pelo_}
      <rect x="66" y="77" width="18" height="4.5" rx="2.25" fill="${pelo}"/>
      <rect x="116" y="77" width="18" height="4.5" rx="2.25" fill="${pelo}"/>
      <ellipse cx="76" cy="95" rx="9.5" ry="10.5" fill="#111"/>
      <ellipse cx="124" cy="95" rx="9.5" ry="10.5" fill="#111"/>
      <circle cx="79.5" cy="90.5" r="2.8" fill="#fff"/>
      <circle cx="127.5" cy="90.5" r="2.8" fill="#fff"/>
      <ellipse cx="100" cy="110" rx="3.4" ry="2.2" fill="${tono(piel, -0.18)}"/>
      <ellipse cx="62" cy="112" rx="8" ry="4.5" fill="#e0675a" opacity="0.14"/>
      <ellipse cx="138" cy="112" rx="8" ry="4.5" fill="#e0675a" opacity="0.14"/>
      ${gorro}`;

    const defs = `
      <defs>
        <linearGradient id="${id}v" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stop-color="#fff" stop-opacity="0.14"/>
          <stop offset="0.45" stop-color="#fff" stop-opacity="0"/>
          <stop offset="1" stop-color="#000" stop-opacity="0.16"/>
        </linearGradient>
        <radialGradient id="${id}c" cx="0.35" cy="0.25" r="0.85">
          <stop offset="0" stop-color="#fff" stop-opacity="0.28"/>
          <stop offset="0.5" stop-color="#fff" stop-opacity="0"/>
          <stop offset="1" stop-color="#000" stop-opacity="0.12"/>
        </radialGradient>
        <linearGradient id="${id}m" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#f8f9fa"/>
          <stop offset="0.45" stop-color="#b6bbbf"/>
          <stop offset="0.62" stop-color="#eef0f1"/>
          <stop offset="1" stop-color="#868c90"/>
        </linearGradient>
        <radialGradient id="${id}g" cx="0.4" cy="0.35" r="0.75">
          <stop offset="0" stop-color="#557482"/>
          <stop offset="0.6" stop-color="#1c2b32"/>
          <stop offset="1" stop-color="#0a1013"/>
        </radialGradient>
        <filter id="${id}s" x="-30%" y="-30%" width="160%" height="160%"><feDropShadow dx="0.3" dy="0.5" stdDeviation="0.4" flood-color="#000" flood-opacity="0.45"/></filter>
        <linearGradient id="${id}l" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0" stop-color="#fff" stop-opacity="0.35"/>
          <stop offset="0.4" stop-color="#fff" stop-opacity="0"/>
          <stop offset="0.6" stop-color="#000" stop-opacity="0"/>
          <stop offset="1" stop-color="#000" stop-opacity="0.85"/>
        </linearGradient>
        <clipPath id="${id}b"><path d="M146 128.8 Q153.5 130.8 156 140 L160 190 H146 Z"/></clipPath>
        <clipPath id="${id}p"><rect x="32" y="14" width="136" height="122" rx="54"/><path d="M71 112 H129 L134 134 Q100 143 66 134 Z"/></clipPath>
        <clipPath id="${id}d"><path d="M31 76 Q28 4 100 4 Q172 4 169 76 Z"/></clipPath>
      </defs>`;

    const titulo = opciones.titulo ? `<title>${opciones.titulo}</title>` : '';
    // entero: el muñeco completo, ajustado a su silueta y sin sombra en el suelo (para iconos)
    const caja = opciones.cabeza ? '22 0 156 156' : opciones.entero ? '26 2 148 283' : '0 0 200 296';
    const aria = opciones.titulo ? 'role="img"' : 'aria-hidden="true"';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${caja}" ${aria} focusable="false">${titulo}${defs}${opciones.cabeza ? '' : cuerpo}<g class="avatar-cabeza">${cabeza}</g></svg>`;
  };

  /**
   * Fondos del avatar (caricatura). Cada uno se dibuja en una caja de 400×400; «oscuro» indica si el texto
   * que va encima debe ser claro. El orden de la lista es el de las flechas del creador.
   */
  const rep = (n, f) => Array.from({ length: n }, (_, i) => f(i)).join('');
  const FONDOS = {
    hueso: {
      nombre: 'Estudio', oscuro: false,
      dibujo: (u) => `<defs><radialGradient id="${u}g" cx="0.5" cy="0.32" r="0.8"><stop offset="0" stop-color="#ffffff"/><stop offset="0.6" stop-color="#efece6"/><stop offset="1" stop-color="#e4e0d8"/></radialGradient></defs>
        <rect width="400" height="400" fill="url(#${u}g)"/>`,
    },
    estadio: {
      nombre: 'Estadio', oscuro: true,
      dibujo: (u) => `<defs><linearGradient id="${u}c" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#0d1a33"/><stop offset="1" stop-color="#1f3a63"/></linearGradient>
          <radialGradient id="${u}f" cx="0.5" cy="0.5" r="0.5"><stop offset="0" stop-color="#fffbe0" stop-opacity="0.95"/><stop offset="1" stop-color="#fffbe0" stop-opacity="0"/></radialGradient></defs>
        <rect width="400" height="400" fill="url(#${u}c)"/>
        ${[60, 340].map((x) => `<rect x="${x - 2}" y="40" width="4" height="120" fill="#4a5875"/><rect x="${x - 22}" y="28" width="44" height="18" rx="3" fill="#dfe6f2"/><circle cx="${x}" cy="37" r="70" fill="url(#${u}f)"/>`).join('')}
        <path d="M0 150 Q200 120 400 150 V275 H0 Z" fill="#2b3446"/>
        ${rep(6, (r) => `<path d="M0 ${162 + r * 19} Q200 ${132 + r * 19} 400 ${162 + r * 19}" stroke="#3b465c" stroke-width="2" fill="none"/>`)}
        ${rep(150, (i) => { const x = (i * 97 + ((i * i) % 53) * 7) % 400, y = 162 + ((i * 61 + i * i * 7) % 104); const c = ['#c8102e', '#f6c518', '#ffffff', '#3d7bd9', '#e6e6e6'][i % 5]; return `<circle cx="${x}" cy="${y - Math.sin((x / 400) * Math.PI) * 30}" r="3.2" fill="${c}" opacity="0.85"/>`; })}
        <rect y="268" width="400" height="132" fill="#2f8a3c"/>
        ${rep(8, (i) => `<rect x="${i * 50}" y="268" width="25" height="132" fill="#2a7d36"/>`)}
        <path d="M0 330 H400 M200 268 V400" stroke="#e9f5e9" stroke-width="3" opacity="0.8"/>
        <ellipse cx="200" cy="330" rx="62" ry="22" fill="none" stroke="#e9f5e9" stroke-width="3" opacity="0.8"/>`,
    },
    barrio: {
      nombre: 'Barrio inglés', oscuro: false,
      dibujo: (u) => `<rect width="400" height="400" fill="#c9d3dc"/>
        <ellipse cx="90" cy="70" rx="60" ry="20" fill="#e8edf1"/><ellipse cx="300" cy="50" rx="70" ry="22" fill="#e8edf1"/>
        ${rep(4, (i) => { const x = i * 100; return `<rect x="${x}" y="110" width="100" height="200" fill="${['#9b4a35', '#a6523b', '#93432f', '#a24d37'][i]}"/>
          ${rep(9, (r) => `<path d="M${x} ${118 + r * 21} H${x + 100}" stroke="#7d3626" stroke-width="1.4" opacity="0.6"/>`)}
          <rect x="${x + 70}" y="88" width="16" height="26" fill="#7d3626"/><rect x="${x + 67}" y="84" width="22" height="6" fill="#5e2a1e"/>
          <path d="M${x - 2} 112 L${x + 50} 96 L${x + 102} 112 Z" fill="#454c55"/>
          <rect x="${x + 16}" y="135" width="28" height="36" fill="#cfe1ee" stroke="#fff" stroke-width="4"/><path d="M${x + 30} 135 V171 M${x + 16} 153 H${x + 44}" stroke="#fff" stroke-width="2.5"/>
          <rect x="${x + 58}" y="135" width="28" height="36" fill="#cfe1ee" stroke="#fff" stroke-width="4"/><path d="M${x + 72} 135 V171 M${x + 58} 153 H${x + 86}" stroke="#fff" stroke-width="2.5"/>
          <rect x="${x + 16}" y="205" width="30" height="40" fill="#cfe1ee" stroke="#fff" stroke-width="4"/>
          <rect x="${x + 58}" y="198" width="28" height="56" rx="13" fill="${['#1f3d6b', '#2f6b46', '#1d1d1d', '#7a1e2b'][i]}" stroke="#fff" stroke-width="3"/>
          <circle cx="${x + 80}" cy="228" r="2" fill="#e3c36b"/>`; })}
        <rect y="300" width="400" height="22" fill="#8a8f96"/><rect y="322" width="400" height="78" fill="#5d636b"/>
        <path d="M0 322 H400" stroke="#4c5158" stroke-width="3"/>
        <rect x="18" y="210" width="34" height="90" rx="3" fill="#c8102e"/><rect x="22" y="222" width="26" height="60" fill="#e14b5b"/><rect x="18" y="204" width="34" height="10" rx="3" fill="#a50d26"/>
        <rect x="350" y="170" width="5" height="130" fill="#2b2b2b"/><rect x="342" y="160" width="21" height="16" rx="3" fill="#2b2b2b"/><rect x="345" y="163" width="15" height="10" fill="#f8e7a0"/>`,
    },
    pub: {
      nombre: 'Pub', oscuro: true,
      dibujo: (u) => `<defs><radialGradient id="${u}l" cx="0.5" cy="0.2" r="0.7"><stop offset="0" stop-color="#ffcf7a" stop-opacity="0.45"/><stop offset="1" stop-color="#ffcf7a" stop-opacity="0"/></radialGradient></defs>
        <rect width="400" height="400" fill="#3a2414"/>
        ${rep(10, (i) => `<rect x="${i * 40}" y="0" width="40" height="230" fill="${i % 2 ? '#43291a' : '#3a2414'}"/>`)}
        <rect y="40" width="400" height="10" fill="#26170c"/>
        ${[70, 200, 330].map((x) => `<rect x="${x - 50}" y="70" width="100" height="6" fill="#26170c"/>${rep(6, (k) => `<rect x="${x - 44 + k * 16}" y="${46 + (k % 2) * 4}" width="10" height="${24 - (k % 2) * 4}" rx="3" fill="${['#2f6b46', '#7a4a1e', '#3d5a80', '#a3361f', '#c9a227', '#2f6b46'][k]}"/>`)}`).join('')}
        <rect x="150" y="90" width="100" height="56" rx="6" fill="#174d2c" stroke="#c9a227" stroke-width="4"/>
        <text x="200" y="127" text-anchor="middle" font-family="Georgia, serif" font-weight="700" font-size="26" fill="#e6c766">PUB</text>
        <circle cx="330" cy="150" r="30" fill="#1d1d1d"/><circle cx="330" cy="150" r="22" fill="#e6dcc4"/><circle cx="330" cy="150" r="14" fill="#c8102e"/><circle cx="330" cy="150" r="6" fill="#2f6b46"/>
        <circle cx="200" cy="0" r="260" fill="url(#${u}l)"/>
        <rect y="230" width="400" height="20" fill="#5c3a20"/><rect y="250" width="400" height="150" fill="#4a2e18"/>
        ${rep(5, (i) => `<rect x="${30 + i * 80}" y="262" width="40" height="130" fill="#563620"/>`)}
        ${[120, 165, 210].map((x, i) => `<rect x="${x}" y="196" width="10" height="36" rx="3" fill="#d9d9d9"/><rect x="${x - 4}" y="188" width="18" height="10" rx="3" fill="${['#c8102e', '#1d1d1d', '#2f6b46'][i]}"/>`).join('')}
        <path d="M262 200 h24 l-3 32 h-18 Z" fill="#e8a83a" opacity="0.9"/><path d="M262 200 h24 v6 h-24 Z" fill="#fff6dc"/>`,
    },
    restaurante: {
      nombre: 'Restaurante', oscuro: true,
      dibujo: (u) => `<defs><radialGradient id="${u}v" cx="0.5" cy="0.45" r="0.6"><stop offset="0" stop-color="#ffd98a" stop-opacity="0.35"/><stop offset="1" stop-color="#ffd98a" stop-opacity="0"/></radialGradient></defs>
        <rect width="400" height="400" fill="#123326"/>
        ${rep(5, (i) => `<rect x="${12 + i * 80}" y="40" width="56" height="200" fill="none" stroke="#c9a227" stroke-width="2" opacity="0.55"/>`)}
        <rect x="150" y="70" width="100" height="72" fill="#2a1d14" stroke="#c9a227" stroke-width="6"/><path d="M160 130 L185 100 L205 118 L222 96 L240 130 Z" fill="#4c6f5e"/>
        <path d="M200 0 V30" stroke="#c9a227" stroke-width="2"/>
        <path d="M168 30 Q200 54 232 30" fill="none" stroke="#c9a227" stroke-width="3"/>
        ${rep(5, (i) => `<path d="M${172 + i * 14} 36 l0 12" stroke="#fff3c4" stroke-width="2"/><circle cx="${172 + i * 14}" cy="50" r="3" fill="#fff3c4"/>`)}
        <circle cx="200" cy="170" r="240" fill="url(#${u}v)"/>
        <rect y="240" width="400" height="160" fill="#2a1d14"/>
        <path d="M30 262 H370 L390 400 H10 Z" fill="#f4f1ea"/>
        <path d="M30 262 H370" stroke="#d9d3c4" stroke-width="3"/>
        ${[95, 305].map((x) => `<path d="M${x - 9} 214 Q${x} 240 ${x + 9} 214 Z" fill="#7a1e2b" opacity="0.85"/><path d="M${x - 9} 214 Q${x} 240 ${x + 9} 214" fill="none" stroke="#d8e2e8" stroke-width="2"/><path d="M${x} 236 V258 M${x - 8} 258 H${x + 8}" stroke="#d8e2e8" stroke-width="2"/>`).join('')}
        ${[160, 240].map((x) => `<rect x="${x - 4}" y="222" width="8" height="38" fill="#f7f2e2"/><path d="M${x} 210 q4 6 0 12 q-4 -6 0 -12" fill="#ffc94d"/>`).join('')}`,
    },
    discoteca: {
      nombre: 'Discoteca', oscuro: true,
      dibujo: (u) => `<rect width="400" height="400" fill="#140a26"/>
        ${['#ff3db8', '#2fd2ff', '#b6ff3a', '#ffb02f'].map((c, i) => `<path d="M200 70 L${[-60, 120, 280, 460][i]} 400 L${[30, 200, 360, 540][i]} 400 Z" fill="${c}" opacity="0.16"/>`).join('')}
        <path d="M200 0 V40" stroke="#888" stroke-width="2"/>
        <circle cx="200" cy="70" r="30" fill="#bfc4cc"/>
        ${rep(6, (r) => rep(6, (k) => `<rect x="${172 + k * 9.5}" y="${42 + r * 9.5}" width="8" height="8" fill="${(r + k) % 3 ? '#e6eaf0' : '#8b93a1'}" opacity="${Math.hypot(176 + k * 9.5 - 200, 46 + r * 9.5 - 70) < 28 ? 1 : 0}"/>`))}
        ${rep(14, (i) => `<circle cx="${(i * 73) % 400}" cy="${30 + ((i * 41) % 200)}" r="2.5" fill="${['#ff3db8', '#2fd2ff', '#b6ff3a', '#fff'][i % 4]}"/>`)}
        <rect y="270" width="400" height="130" fill="#1d1236"/>
        ${rep(5, (r) => rep(8, (k) => `<rect x="${k * 50 + 2}" y="${272 + r * 26}" width="46" height="23" fill="${['#ff3db8', '#2fd2ff', '#b6ff3a', '#ffb02f', '#7b3dff'][(r * 3 + k) % 5]}" opacity="${0.25 + ((r + k) % 3) * 0.18}"/>`))}`,
    },
    metro: {
      nombre: 'Metro de Londres', oscuro: false,
      dibujo: (u) => `<rect width="400" height="400" fill="#efede6"/>
        ${rep(14, (r) => rep(9, (k) => `<rect x="${k * 46 - (r % 2) * 23}" y="${r * 18}" width="44" height="16" rx="2" fill="#f7f6f1" stroke="#dcd8cc" stroke-width="1"/>`))}
        <rect y="40" width="400" height="14" fill="#1f4aa8"/>
        <circle cx="200" cy="130" r="58" fill="none" stroke="#dc241f" stroke-width="20"/>
        <rect x="112" y="116" width="176" height="28" fill="#1f4aa8"/>
        <text x="200" y="137" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="18" letter-spacing="2" fill="#fff">STONESHP</text>
        <rect y="262" width="400" height="20" fill="#3a3a3a"/>
        <rect y="282" width="400" height="16" fill="#f6c518"/>
        <rect y="298" width="400" height="102" fill="#6e6a62"/>
        ${rep(10, (i) => `<rect x="${i * 44}" y="306" width="30" height="6" fill="#5d5a53"/>`)}`,
    },
  };
  /**
   * Detalles del equipo en cada fondo cuando el avatar lleva una camiseta de fútbol: colores, escudo y nombre
   * del club repartidos por la escena (gradas, banderines, carteles, bufandas…).
   */
  const datosEquipo = (clave) => {
    const E = EQUIPOS[clave];
    if (!E) return null;
    const P = E.patron || {};
    const c1 = E.base, c2 = P.color || P.der || P.izq || E.ribete, c3 = E.ribete;
    const sobre = (c) => (luz(c) < 0.55 ? '#ffffff' : '#141414');
    return { E, c1, c2: c2.toLowerCase() === c1.toLowerCase() ? c3 : c2, c3, sobre, nombre: E.nombre.toUpperCase() };
  };
  const escudoEn = (T, u, x, y, s) => `<g transform="translate(${x} ${y}) scale(${s})">${T.E.escudo(u)}</g>`;
  const banderines = (T, y, n = 12) => {
    const w = 400 / n;
    return `<path d="M0 ${y} Q200 ${y + 16} 400 ${y}" stroke="#2b2b2b" stroke-width="1.5" fill="none"/>` +
      Array.from({ length: n }, (_, i) => { const x = i * w, yy = y + Math.sin(((i + 0.5) / n) * Math.PI) * 8;
        return `<path d="M${x + 2} ${yy} L${x + w - 2} ${yy} L${x + w / 2} ${yy + 22} Z" fill="${i % 2 ? T.c2 : T.c1}" stroke="rgb(0 0 0 / 0.25)" stroke-width="0.8"/>`; }).join('');
  };
  const bufanda = (T, x, y, w, h, rot = 0) => `<g transform="translate(${x} ${y}) rotate(${rot})">
      ${Array.from({ length: Math.ceil(w / 14) }, (_, i) => `<rect x="${i * 14}" y="0" width="14" height="${h}" fill="${i % 2 ? T.c2 : T.c1}"/>`).join('')}
      <rect x="0" y="0" width="${w}" height="${h}" fill="none" stroke="rgb(0 0 0 / 0.3)" stroke-width="1"/>
      ${Array.from({ length: Math.floor(h / 4) }, (_, i) => `<path d="M${w} ${2 + i * 4} h7 M0 ${2 + i * 4} h-7" stroke="${T.c1}" stroke-width="1.5"/>`).join('')}
    </g>`;
  const cartel = (T, u, x, y, w, h) => `<rect x="${x}" y="${y}" width="${w}" height="${h}" fill="${T.c1}" stroke="#f4f1ea" stroke-width="3"/>
      <rect x="${x}" y="${y + h * 0.72}" width="${w}" height="${h * 0.28}" fill="${T.c2}"/>
      ${escudoEn(T, u, x + w / 2, y + h * 0.38, Math.min(w, h) / 34)}
      <text x="${x + w / 2}" y="${y + h * 0.92}" text-anchor="middle" font-family="Arial Black, Arial, sans-serif" font-weight="900" font-size="${Math.min(9, (w / T.nombre.length) * 1.5)}" fill="${T.sobre(T.c2)}">${T.nombre}</text>`;

  const AFICION = {
    hueso: (u, T) => `<g opacity="0.07">${escudoEn(T, u + 'w', 200, 175, 14)}</g>`,
    estadio: (u, T) => `
      <path d="M0 150 Q200 120 400 150 V275 H0 Z" fill="${T.c1}" opacity="0.28"/>
      ${Array.from({ length: 240 }, (_, i) => { const x = (i * 97 + ((i * i) % 53) * 7) % 400, y = 160 + ((i * 61 + i * i * 7) % 104); const c = [T.c1, T.c2, T.c1, T.c3][i % 4]; return `<circle cx="${x}" cy="${y - Math.sin((x / 400) * Math.PI) * 30}" r="3.4" fill="${c}"/>`; }).join('')}
      <rect x="0" y="236" width="400" height="24" fill="${T.c1}"/><rect x="0" y="236" width="400" height="3" fill="${T.c2}"/><rect x="0" y="257" width="400" height="3" fill="${T.c2}"/>
      <text x="200" y="253" text-anchor="middle" font-family="Arial Black, Arial, sans-serif" font-weight="900" font-size="14" letter-spacing="3" fill="${T.sobre(T.c1)}">${T.nombre}</text>
      ${escudoEn(T, u + 'a', 30, 248, 0.55)}${escudoEn(T, u + 'b', 370, 248, 0.55)}
      <rect x="150" y="58" width="100" height="56" rx="4" fill="#0b0f18" stroke="#5b6680" stroke-width="3"/>
      ${escudoEn(T, u + 'c', 200, 86, 1.25)}`,
    barrio: (u, T) => `
      ${banderines(T, 92)}
      ${cartel(T, u + 'p1', 112, 200, 40, 52)}
      ${cartel(T, u + 'p2', 304, 200, 40, 52)}
      ${bufanda(T, 216, 172, 56, 12, 2)}
      <rect x="352" y="182" width="2" height="38" fill="#2b2b2b"/><path d="M354 184 h26 v16 h-26 Z" fill="${T.c1}"/><path d="M354 192 h26" stroke="${T.c2}" stroke-width="4"/>`,
    pub: (u, T) => `
      ${banderines(T, 2, 10)}
      <rect x="150" y="90" width="100" height="56" rx="6" fill="${T.c1}" stroke="#c9a227" stroke-width="4"/>
      ${escudoEn(T, u + 's', 176, 118, 1.05)}
      <text x="222" y="125" text-anchor="middle" font-family="Georgia, serif" font-weight="700" font-size="20" fill="${T.sobre(T.c1)}">PUB</text>
      ${bufanda(T, 0, 226, 400, 12)}
      <rect x="28" y="100" width="56" height="70" fill="#2a1a0e" stroke="#c9a227" stroke-width="3"/>${escudoEn(T, u + 'f', 56, 135, 2)}`,
    restaurante: (u, T) => `
      <rect x="150" y="70" width="100" height="72" fill="${T.c1}" stroke="#c9a227" stroke-width="6"/>
      ${escudoEn(T, u + 'q', 200, 106, 2.6)}
      <path d="M170 262 H230 L236 400 H164 Z" fill="${T.c1}" opacity="0.9"/><path d="M170 262 H230" stroke="${T.c2}" stroke-width="4"/>
      ${[24, 376].map((x, k) => `<rect x="${x - 1.5}" y="60" width="3" height="170" fill="#c9a227"/><path d="M${x + (k ? -2 : 2)} 64 h${k ? -34 : 34} v54 l${k ? 17 : -17} -10 l${k ? 17 : -17} 10 Z" fill="${T.c1}" stroke="${T.c2}" stroke-width="2"/>`).join('')}`,
    discoteca: (u, T) => `
      ${[T.c1, T.c2, T.c3, T.c1].map((c, i) => `<path d="M200 70 L${[-60, 120, 280, 460][i]} 400 L${[30, 200, 360, 540][i]} 400 Z" fill="${c}" opacity="0.2"/>`).join('')}
      <rect x="128" y="150" width="144" height="88" rx="6" fill="#0a0614" stroke="${T.c1}" stroke-width="3"/>
      ${escudoEn(T, u + 'd', 200, 186, 2.2)}
      <text x="200" y="230" text-anchor="middle" font-family="Arial Black, Arial, sans-serif" font-weight="900" font-size="10" letter-spacing="2" fill="${T.c1 === '#ffffff' ? T.c2 : T.c1}">${T.nombre}</text>
      ${Array.from({ length: 40 }, (_, i) => `<rect x="${(i % 8) * 50 + 2}" y="${272 + Math.floor(i / 8) * 26}" width="46" height="23" fill="${[T.c1, T.c2, T.c3][(Math.floor(i / 8) + i) % 3]}" opacity="${0.22 + ((i * 7) % 3) * 0.14}"/>`).join('')}`,
    metro: (u, T) => `
      <circle cx="200" cy="130" r="58" fill="none" stroke="${T.c1}" stroke-width="20"/><circle cx="200" cy="130" r="68" fill="none" stroke="rgb(0 0 0 / 0.2)" stroke-width="1"/>
      <rect x="112" y="116" width="176" height="28" fill="${T.c2}"/>
      <text x="200" y="137" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-weight="700" font-size="18" letter-spacing="2" fill="${T.sobre(T.c2)}">STONESHP</text>
      ${cartel(T, u + 'm1', 22, 150, 70, 96)}
      ${cartel(T, u + 'm2', 308, 150, 70, 96)}`,
  };
  const ORDEN_FONDOS = Object.keys(FONDOS);
  let contadorFondo = 0;
  /** Fondo completo como SVG (para el creador) */
  const fondoSvg = (clave, equipo = '') => {
    const k = FONDOS[clave] ? clave : 'hueso'; const u = 'af' + ++contadorFondo;
    const T = datosEquipo(equipo);
    const extra = T && AFICION[k] ? AFICION[k](u + 't', T) : '';
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true" focusable="false">${FONDOS[k].dibujo(u)}${extra}</svg>`;
  };
  /** Fondo como imagen de fondo CSS (para los círculos del perfil) */
  const fondoUri = (clave, equipo) => `url("data:image/svg+xml;charset=utf-8,${encodeURIComponent(fondoSvg(clave, equipo))}")`;

  /** Sustituye los iconos de perfil por la cabeza del avatar guardado (o los deja como estaban). */
  const pintarCabezas = (soloNuevos = false) => {
    const c = leer();
    document.querySelectorAll(soloNuevos ? '[data-avatar-cabeza]:not([data-avatar-pintado])' : '[data-avatar-cabeza]').forEach((el) => {
      el.dataset.avatarPintado = '';
      if (!el.dataset.avatarOriginal) el.dataset.avatarOriginal = el.innerHTML;
      if (c) {
        el.innerHTML = el.dataset.avatarCabeza === 'entero'
          ? `<span class="avatar-entero-mini">${svg(c, { entero: true })}</span>`
          : `<span class="avatar-cabeza-mini">${svg(c, { cabeza: true })}</span>`;
        el.classList.add('con-avatar');
        // El círculo lleva el fondo elegido (el «Estudio» es el blanco de siempre)
        if (c.fondo && (c.fondo !== 'hueso' || c.camiseta.equipo)) el.style.setProperty('background', `${fondoUri(c.fondo, c.camiseta.equipo)} center / cover`, 'important');
        else el.style.removeProperty('background');
      } else {
        el.style.removeProperty('background');
        el.innerHTML = el.dataset.avatarOriginal;
        el.classList.remove('con-avatar');
      }
    });
  };

  window.StoneshpAvatar = { FONDOS, ORDEN_FONDOS, fondoSvg, fondoUri, CLAVE, PIELES, PELOS, ROPA, ZAPAS, EQUIPOS, miniCamiseta, PREDETERMINADO, completar, leer, guardar, borrar, svg, pintarCabezas };

  const iniciar = () => {
    pintarCabezas();
    // El tema vuelve a montar partes de la cabecera (p. ej. el menú lateral): se pintan las que aparezcan nuevas
    let pendiente = 0;
    new MutationObserver(() => {
      if (pendiente) return;
      pendiente = requestAnimationFrame(() => {
        pendiente = 0;
        if (document.querySelector('[data-avatar-cabeza]:not([data-avatar-pintado])')) pintarCabezas(true);
      });
    }).observe(document.body, { childList: true, subtree: true });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
  window.addEventListener('storage', (e) => e.key === CLAVE && pintarCabezas());
})();
